import React, { useEffect, useState, useRef } from 'react';
import type { StoredPlaylists } from '@ds-components/Main/heroselect';
import { dotaSpottoTheme } from '@ds-components/General/dotaspottotheme';
import * as Analytics from "@ds-services/analytics/analytics";

import { Container, Box } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import CardActionArea from '@mui/material/CardActionArea';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
// import Stack from '@mui/material/Stack';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { keyframes } from '@mui/material';

const playlistCardWidth = {
   xs: 250,
   md: 360,
};


declare global {
   interface Window {
      onSpotifyIframeApiReady(something: any): void;
      SpotifyIframeConfig: {
         host: string,
         loading: string,
      };
   }
}
export const PlaylistPlayer: React.FC<{ playlists: StoredPlaylists[] | null }> = ({ playlists }) => {
   if (!playlists || playlists.length === 0) return <></>

   const getMostRecentPlaylist = playlists[0] ?? null;
   const [currentPlaylist, setCurrentPlaylist] = useState(getMostRecentPlaylist);
   const [isPlaylistPlaying, setIsPlaylistPlaying] = useState(false)
   const embedControllerRef = useRef<any | null>(null);
   const currentPlaylistRef = useRef<StoredPlaylists | null>(currentPlaylist);
   const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});

   useEffect(() => {
      currentPlaylistRef.current = currentPlaylist;
   }, [currentPlaylist]);
   const spotifyIframeCallback = (EmbedController: any) => {

   }
   useEffect(() => {
      const checkSpotifyEmbedScript = document.querySelector('#spotify-embed-script');
      if (!checkSpotifyEmbedScript) {
         const script = document.createElement('script');
         script.id = 'spotify-embed-script';
         script.src = 'https://open.spotify.com/embed/iframe-api/v1';
         script.async = true;
         document.head.appendChild(script);
      }


      window.onSpotifyIframeApiReady = (IFrameAPI) => {
         const getPlaylistEmbedElement = document.getElementById("playlistPlayerEmbed");
         if (getPlaylistEmbedElement && currentPlaylist) {
            IFrameAPI.createController(getPlaylistEmbedElement, {
               uri: currentPlaylist.uri,
            }, (controller: any) => {
               embedControllerRef.current = controller;
               controller.addListener('playback_started', (state: any) => {
                  const playlist = currentPlaylistRef.current;
                  if (playlist && playlist.url) {
                     Analytics.trackPlaylistPlayed(playlist.url);
                  }
               });
            });
         }
      }
   }, [])
   useEffect(() => {
      if (embedControllerRef.current && currentPlaylist?.uri) {
         embedControllerRef.current.loadUri(currentPlaylist.uri);
      }
      Object.entries(videoRefs.current).forEach(([id, video]) => {
         if (!video) return;
         if (id === currentPlaylist?.id) {
            video.play().catch((e) => console.warn(`Could not play video for ${id}`, e));
         } else {
            video.pause();
         }
      });
   }, [currentPlaylist, playlists])

   return (
      <Container sx={{
         display: "flex",
         flexFlow: {
            md: "row nowrap",
            xs: "column nowrap"
         },
         alignItems: "stretch",
         width: '100%',
         py: 2,
         gap: 2,
         maxWidth: {
            xs: "100%"
         },
         // maxHeight: {
         //    md: '50vh',
         // },
         minHeight: {
            md: 0,
         },
         backgroundColor: dotaSpottoTheme.primary
      }}>
         {currentPlaylist && (
            <Box sx={{
               width: {
                  md: "25%"
               },
               "& iframe": {
                  height: '100%',
                  aspectRatio: {
                     md: "unset",
                     xs: " 1 / 1",
                  }
               }
            }}>
               <Box id="playlistPlayerEmbed"></Box>
            </Box>
         )}
         <Box id="spotifyPlaylistGenerated" sx={{
            display: 'flex',
            flexFlow: 'row nowrap',
            flexBasis: 'fit-content',
            gap: {
               md: 6,
               xs: 2
            },
            width: {
               md: "100%"
            },
            alignItems: 'flex-start',
            overflow: 'auto',
            scrollSnapType: 'x mandatory', // Enable snapping
         }}>
            {playlists.map((playlist: StoredPlaylists, index: Number) => {
               const heroVideo = playlist.hero?.video;
               const playlistDescription = playlist.description;
               const playlistName = playlist.name;
               const playlistId = playlist.id;
               let nowPlaying = false;
               if (playlist === currentPlaylist) {
                  nowPlaying = true;
               }
               console.log(`🎠grim__nowPlaying==>==>==>${nowPlaying} -- ${playlistId}`);
               const fadeIn = keyframes`
               from {
                  opacity: 1;
                  background-color: gold
                  color: black;
                  }
                  to {
                  opacity: 0.75;
                  background-color: black;
                  color: white;
               }
               `;
               return (
                  <Card
                     key={playlistId}
                     raised={nowPlaying}
                     data-active={nowPlaying ? '' : undefined}
                     ref={nowPlaying ? (ref) => {
                        if (ref && typeof ref.scrollIntoView === 'function') {
                           ref.scrollIntoView({
                              behavior: 'smooth',
                              block: 'nearest',
                              inline: 'start',
                           });
                        }
                     } : undefined}
                     sx={{
                        display: 'inline-flex',
                        flexFlow: 'column nowrap',
                        // flex: 1,
                        zIndex: 1,
                        position: 'relative',
                        width: playlistCardWidth,
                        minWidth: playlistCardWidth,
                        maxWidth: playlistCardWidth,
                        flex: '0 0 auto',
                        backgroundColor: 'black',
                        scrollSnapAlign: 'start',
                        border: {
                           md: '2px solid transparent',
                           xs: 'none !important',
                        },
                        filter: 'grayscale(0)',
                        opacity: 1,
                        transition: 'filter 0.35s ease, opacity 0.35s ease, border 0.35s ease',
                        " .now-playing": {
                           opacity: 0,
                           position: "absolute",
                           top: '10px',
                           left: 0,
                           width: "100%",
                           height: "auto",
                           display: "flex",
                           justifyContent: "center",
                           alignItems: "center",
                           gap: 1,
                           fontSize: "1.2rem",
                           fontWeight: "bold",
                           color: "gold",
                           transition: "opacity 0.35s ease",
                           fontFamily: dotaSpottoTheme.font,
                        },
                        '&:not([data-active])': {
                           filter: 'grayscale(100%)',
                           opacity: 0.5,
                           transition: 'filter 0.35s ease, opacity 0.35s ease',
                        },
                        '&[data-active]': {
                           border: '2px solid gold',
                           " .now-playing": {
                              opacity: 1
                           }
                        },
                     }}
                  >
                     <CardActionArea
                        sx={{
                           flex: 1,
                           width: '100%',
                        }}
                        onClick={() => {
                           if (currentPlaylist !== playlist) {
                              setCurrentPlaylist(playlist)
                           }
                        }}
                     >
                        <Box className="now-playing">
                           <Chip
                              label="Now Previewing"
                              size='small'
                              sx={{
                                 backgroundColor: 'gold',
                                 border: '1px solid gold',
                                 boxShadow: '0 0 5px 1px rgba(0,0,0,0.5)',
                                 animation: `${fadeIn} 2.5s ease-in-out infinite alternate`, // Loop animation forever
                                 "& .MuiChip-label": {
                                    fontFamily: dotaSpottoTheme.font
                                 }
                              }}
                              icon={
                                 <MusicNoteIcon sx={{
                                    borderRadius: '50%',
                                    color: 'white !important',
                                    backgroundColor: 'black',
                                 }} />
                              }
                           />
                        </Box>
                        {playlist.image && (
                           <CardMedia
                              component="img"
                              image={playlist.image}
                              alt={playlist.name}
                              sx={{
                                 display: 'block',
                                 width: '100%',
                                 aspectRatio: '1 / 1',
                                 objectFit: "cover",
                              }}
                           />
                        )}
                        <CardContent>
                           <Typography gutterBottom component="div"
                              sx={{
                                 fontSize: '1rem',
                                 lineHeight: 1.1,
                                 fontFamily: dotaSpottoTheme.font2,
                                 fontWeight: 'bold',
                                 mb: 2.5,
                              }}
                           >
                              {playlistName}
                           </Typography>
                           {playlistDescription && (
                              <Typography gutterBottom component="div"
                                 sx={{
                                    backgroundColor: 'transparent',
                                    fontSize: {
                                       md: "1rem",
                                       xs: ".85rem",
                                    }
                                 }}
                              >
                                 {playlistDescription}
                              </Typography>
                           )}
                        </CardContent>
                     </CardActionArea>
                     <CardActions sx={{
                        marginTop: "auto",
                        width: 'auto',
                     }}>
                        <Button
                           variant='contained'
                           size="small"
                           color="primary"
                           sx={{
                              flex: 1,
                              textAlign: "center",
                           }}
                           onClick={() => {
                              setCurrentPlaylist(playlist)
                           }}
                        >
                           Preview
                        </Button>
                        <Link
                           href={playlist.url}
                           sx={{
                              flex: 1,
                              color: 'gold',
                              textAlign: "center",
                           }}
                        >
                           Open Playlist
                        </Link>
                     </CardActions>
                     {
                        heroVideo && (
                           <Box
                              sx={{
                                 position: "absolute",
                                 zIndex: -1,
                                 width: "100%",
                                 height: "100%",
                                 left: "0",
                                 top: "0",
                                 display: "flex",
                                 justifyContent: "center",
                                 // opacity: 0.35,
                                 filter: "saturate(5) blur(10px) contrast(2) brightness(0.28)",
                                 // transform: "scale(2)",
                              }}>
                              <video
                                 ref={(el) => {
                                    if (playlist.id) {
                                       videoRefs.current[playlist.id] = el;
                                    }
                                 }}
                                 loop
                                 muted
                                 style={{
                                    objectFit: "contain",
                                    flex: "1",
                                    height: "auto"
                                 }}
                                 poster={heroVideo.replace("webm", "png")}
                              >
                                 <source
                                    src={heroVideo}
                                    type="video/webm"
                                 />
                                 Your browser does not support the video tag.
                              </video>
                           </Box>
                        )
                     }
                  </Card>
               )
            })}
         </Box>
      </Container >
   );
}
